import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { CloudLightning, Flame, Mountain, Waves, Snowflake } from "lucide-react";
import { Badge } from "@/components/ui/badge";

type ClimateEvent = {
  id: string;
  title: string;
  categories: string[];
  sources: { id: string; url: string }[];
  lat: number | null;
  lng: number | null;
  date: string | null;
};

const categoryIcons: Record<string, any> = {
  Wildfires: Flame,
  "Severe Storms": CloudLightning,
  Volcanoes: Mountain,
  Floods: Waves,
  "Sea and Lake Ice": Snowflake,
};

const categoryColors: Record<string, string> = {
  Wildfires: "text-orange-400 bg-orange-500/10 border-orange-500/30",
  "Severe Storms": "text-blue-400 bg-blue-500/10 border-blue-500/30",
  Volcanoes: "text-red-400 bg-red-500/10 border-red-500/30",
  Floods: "text-cyan-400 bg-cyan-500/10 border-cyan-500/30",
  "Sea and Lake Ice": "text-sky-300 bg-sky-500/10 border-sky-500/30",
};

const ClimateAnomalies = () => {
  const { data, isLoading } = useQuery({
    queryKey: ["climate-events"],
    queryFn: async () => {
      const { data, error } = await supabase.functions.invoke("fetch-climate");
      if (error) throw error;
      return data as { events: ClimateEvent[]; fetchedAt: string };
    },
    staleTime: 600000,
    refetchInterval: 600000,
    retry: 2,
  });

  const events = data?.events || [];
  const categoryCounts: Record<string, number> = {};
  events.forEach((e) => e.categories.forEach((c) => { categoryCounts[c] = (categoryCounts[c] || 0) + 1; }));

  return (
    <section id="climate" className="py-6 sm:py-8">
      <div className="container mx-auto px-4">
        <div className="mb-4 sm:mb-6">
          <div className="flex items-center gap-2 sm:gap-3 mb-1">
            <CloudLightning className="h-4 w-4 sm:h-5 sm:w-5 text-blue-400" />
            <h2 className="font-mono text-sm sm:text-lg font-bold uppercase tracking-wider text-foreground">
              Climate & Natural Events
            </h2>
          </div>
          <Badge variant="outline" className="text-[9px] font-mono text-muted-foreground">
            NASA EONET — Active Events
          </Badge>
        </div>

        {/* Category summary */}
        <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-3 sm:mb-4">
          {Object.entries(categoryCounts).map(([cat, count]) => {
            const Icon = categoryIcons[cat] || CloudLightning;
            return (
              <div key={cat} className={`flex items-center gap-1 sm:gap-1.5 rounded-md border px-2 py-1 sm:px-2.5 sm:py-1.5 text-[10px] sm:text-xs font-medium ${categoryColors[cat] || "text-muted-foreground bg-secondary border-border"}`}>
                <Icon className="h-3 w-3 sm:h-3.5 sm:w-3.5" />
                {cat}: {count}
              </div>
            );
          })}
        </div>

        {isLoading && !data ? (
          <div className="rounded-lg border border-border bg-card p-6 sm:p-8 text-center text-xs sm:text-sm text-muted-foreground">
            Loading climate data...
          </div>
        ) : (
          <div className="rounded-lg border border-border bg-card overflow-hidden">
            <div className="divide-y divide-border/50 max-h-[350px] sm:max-h-[400px] overflow-y-auto">
              {events.slice(0, 30).map((e) => {
                const cat = e.categories[0] || "Unknown";
                const Icon = categoryIcons[cat] || CloudLightning;
                return (
                  <div key={e.id} className="flex items-center gap-2 sm:gap-3 px-2.5 sm:px-3 py-2 sm:py-2.5 hover:bg-accent/50 transition-colors">
                    <div className={`flex h-6 w-6 sm:h-7 sm:w-7 shrink-0 items-center justify-center rounded-md ${categoryColors[cat]?.split(" ")[1] || "bg-secondary"}`}>
                      <Icon className={`h-3 w-3 sm:h-3.5 sm:w-3.5 ${categoryColors[cat]?.split(" ")[0] || "text-muted-foreground"}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[11px] sm:text-xs font-medium text-foreground truncate">{e.title}</p>
                      <div className="flex items-center gap-1.5 sm:gap-2 mt-0.5 flex-wrap">
                        <span className={`text-[8px] sm:text-[9px] px-1 sm:px-1.5 py-0.5 rounded border ${categoryColors[cat] || "border-border"}`}>{cat}</span>
                        {e.date && <span className="text-[8px] sm:text-[9px] text-muted-foreground">{timeAgo(e.date)}</span>}
                      </div>
                    </div>
                    {e.sources[0] && (
                      <a href={e.sources[0].url} target="_blank" rel="noopener noreferrer" className="text-[9px] text-primary hover:underline shrink-0">
                        Source
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {data?.fetchedAt && (
          <p className="mt-2 text-[10px] font-mono text-muted-foreground text-right">
            Source: NASA EONET · Updated: {new Date(data.fetchedAt).toLocaleTimeString()}
          </p>
        )}
      </div>
    </section>
  );
};

function timeAgo(iso: string) {
  const ms = Date.now() - new Date(iso).getTime();
  const h = Math.floor(ms / 3600000);
  if (h < 1) return `${Math.floor(ms / 60000)}m ago`;
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

export default ClimateAnomalies;
